// A dashboard summary item by reference: the action's own title with its date (for a
// blocker, how long it has blocked) in a right-hand column, and under it the specialist
// that reported it and who it is on. It links to the action on its specialist's page, and
// its dashboard line shows on hover (design-structured-next-actions-and-dates.md 6.1).
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { DashboardRow, RecordedAction } from "../api/hooks/useDashboard";
import { daysSince } from "../api/hooks/useProjectAgentStatus";
import { projectSpecialistPath } from "../routes/learningAgentPaths";
import { formatShortDay } from "../utils/attributeFormat";
import { agentDisplayLabel } from "../utils/runParticipants";
import styles from "./DeploymentTable.module.css";

const recordHref = (row: DashboardRow, agent: string, id: string): string =>
  `${projectSpecialistPath(row.project_id, agent)}#${id}`;

// "Elliot Windmill (National Grid Electricity Distribution)" as the table's On column
// names him: the person, not the organisation.
const personOf = (external: string): string => external.split(" (")[0];

// Who has to act, as the Next actions table's On column says it. A wait names who it is
// on; one written before waits named anyone says Waiting, its event on hover. Their reply
// overdue is ours to chase, and our answer overdue is ours too.
const who = (action: RecordedAction): ReactNode => {
  // reply_overdue: their reply overdue, as rows snapshotted before 13 Oct 2026 carry it.
  if (action.status === "to_chase" || action.status === "reply_overdue")
    return (
      <span className={styles.recordLate}>
        {action.external
          ? `${action.owner}, chase ${personOf(action.external)}`
          : `${action.owner}, reply overdue`}
      </span>
    );
  if (action.status === "answer_overdue")
    return (
      <span className={styles.recordLate}>{action.owner}, reply overdue</span>
    );
  if (action.status === "awaiting_reply")
    return (
      <span className={styles.recordTheirs}>{personOf(action.external)}</span>
    );
  if (action.status === "waiting") {
    const on = action.waits_on;
    return (
      <span
        title={action.waits_for}
        className={on?.on === "them" ? styles.recordTheirs : undefined}
      >
        {on?.name
          ? `Waiting on ${on.name}${on.org ? ` (${on.org})` : ""}`
          : "Waiting"}
      </span>
    );
  }
  if (
    ["planned", "decided", "done", "dropped", "closed", ""].includes(
      action.status,
    )
  )
    return null;
  return <span className={styles.recordOurs}>{action.owner}</span>;
};

const DueDate = ({ action }: { action: RecordedAction }) => (
  <span className={action.late ? styles.recordLate : undefined}>
    {formatShortDay(action.due)}
  </span>
);

export const RecordItem = ({
  row,
  id,
  blocker = false,
}: {
  row: DashboardRow;
  id: string;
  /** A blocker also says how long it has blocked. */
  blocker?: boolean;
}) => {
  const action: RecordedAction | undefined = row.records?.actions[id];
  if (!action) {
    return (
      <li>
        <span className={styles.muted}>no longer open</span>
      </li>
    );
  }
  const line = [
    action.summary && action.summary !== action.title ? action.summary : "",
    action.pick_reason ? `Ranked here: ${action.pick_reason}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  const dated = Boolean(action.due);
  const openDays =
    blocker && action.blocking_since ? daysSince(action.blocking_since) : null;
  // A blocker's right-hand column is how long it has blocked, so its date joins the line.
  const meta = [
    agentDisplayLabel(action.agent),
    who(action),
    blocker && dated ? <DueDate action={action} /> : null,
  ].filter(Boolean);
  return (
    <li>
      <Link
        className={styles.recordLink}
        to={recordHref(row, action.agent, id)}
        title={line || undefined}
      >
        <span className={styles.recordTitle}>{action.title}</span>
        <span className={styles.recordWhen}>
          {blocker
            ? openDays !== null && (
                <span title={`Open ${openDays} days`}>{openDays}d</span>
              )
            : dated && <DueDate action={action} />}
        </span>
        <span className={styles.recordMeta}>
          {meta.map((part, i) => (
            <span key={i}>
              {i > 0 && " · "}
              {part}
            </span>
          ))}
        </span>
      </Link>
    </li>
  );
};
